import { Send } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useResendInvitationMutation } from "../api";
import { DeleteUserConfirm } from "../components/DeleteUserConfirm";

import Icons from "@/components/icons";
import { ActionsCell } from "@/components/ui/v1/table/ActionsCell";
import { DateTimeCell } from "@/components/ui/v1/table/TableCells";
import { ROUTES } from "@/constants/routes";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { Action } from "@/types/table/action";
import { TableColumn } from "@/types/table/tableProps";
import { UserType } from "@/types/user/userType";

export const useUserTableColumns = () => {
  const { t } = useTranslation();
  const { canEdit, canDelete, canManageRole, canDeleteRole } =
    usePermissionMatrix();
  const navigate = useNavigate();
  const resendInvitationMutation = useResendInvitationMutation();
  const { userId: currentUserId } = useAuth();
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    userId: string;
    userName: string;
  }>({ open: false, userId: "", userName: "" });

  const hasAnyActions = canEdit("users") || canDelete("users");

  const userTableColumns: TableColumn[] = [
    {
      header: t("common.email"),
      accessor: "email",
      width: hasAnyActions ? "45%" : "50%",
    },
    {
      header: t("common.role"),
      accessor: "roleName",
      width: "25%",
      sortable: false,
    },
    {
      header: t("common.lastLogin"),
      accessor: "lastLogin",
      Cell: (row: UserType): JSX.Element => (
        <DateTimeCell timestamp={row?.lastLogin || ""} />
      ),
      width: "25%",
    },
    ...(hasAnyActions
      ? [
        {
          header: t("common.actions"),
          textAlign: "right" as const,
          width: "5%",
          Cell: (user: UserType) => {
            const isCurrentUser = user.id === currentUserId;
            const canManageThisUser =
                isCurrentUser || canManageRole(user.roleName);

            const actions: Action[] = [];

            if (canEdit("users") && canManageThisUser) {
              actions.push({
                key: "edit",
                label: t("common.edit"),
                icon: <Icons.EditSquareIcon fontSize={24} />,
                onClick: () => {
                  navigate(user.id.concat("/", ROUTES.edit));
                },
              });
            }

            if (
              canEdit("users") &&
                user.isInvitationPending &&
                canManageRole(user.roleName)
            ) {
              actions.push({
                key: "resendInvitation",
                label: t("admin.userManagement.resendInvitation.action"),
                icon: <Send />,
                onClick: () => resendInvitationMutation.mutate(user.id),
                disabled:
                  resendInvitationMutation.isPending &&
                  resendInvitationMutation.variables === user.id,
              });
            }

            if (
              canDelete("users") &&
                canDeleteRole(user.roleName) &&
                !isCurrentUser
            ) {
              actions.push({
                key: "delete",
                label: t("common.delete"),
                icon: <Icons.DeleteIcon fontSize={24} />,
                onClick: () => {
                  setDeleteDialogState({
                    open: true,
                    userId: user.id,
                    userName:
                        user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.email,
                  });
                },
              });
            }

            return <ActionsCell actions={actions} />;
          },
        },
      ]
      : []),
  ];

  const deleteDialog = deleteDialogState.open && (
    <DeleteUserConfirm
      id={deleteDialogState.userId}
      name={deleteDialogState.userName}
      open={deleteDialogState.open}
      onOpenChange={(open) =>
        setDeleteDialogState((prev) => ({ ...prev, open }))
      }
    />
  );

  return {
    userTableColumns,
    deleteDialog,
  };
};
